// Add or correct an effective-dated term — the core workflow of the data model.
//
// §07: an escalator or a step-down is NOT an edit to the current term, it is an
// additional row with a future effective date, and the term in force is the
// latest row whose effective_date <= today. So "add a term" is the primary
// action here and the form says what a future date will do.
//
// Editing a PAST term stays legal, and deliberately so (`inputs.ts`): fixing a
// mis-keyed split is a real need, and it is safe in the one way that matters —
// a charge freezes its split when the invoice that carries it goes out
// (migration 0013), so correcting a 2025 term restates no invoice that already
// went out. The form says that, because otherwise it looks like history is
// being rewritten.
//
// What an edit here DOES move is every unbilled bill under the agreement: those
// derive their split as of their own due date and re-price on the next read.
// That is the point of editing a term, and the reason a step-up belongs in a
// NEW term with a future effective date rather than in an edit to this one.

import type { ReactNode } from "react";
import { AgreementTermCreate, AgreementTermUpdate, Currency } from "@crm/shared";
import type { AgreementTerm } from "@crm/shared";
import { useAgreementTermMutations } from "../../api/mutations.js";
import { FormDialog } from "../../forms/FormDialog.js";
import { DateField, NumberField, ReadOnlyField, SelectField } from "../../forms/fields.js";
import type { SelectOption } from "../../forms/fields.js";
import { parseDecimal, parseOptionalDecimal, textOf } from "../../forms/parseInput.js";
import { useEditor, type Wire } from "../../forms/useEditor.js";
import { formatPercent } from "../../format/money.js";
import { todayIsoDate } from "../../format/calendarDate.js";

interface TermDraft {
  agreement_id: string;
  effective_date: string;
  impl_split_pct: string;
  recurring_split_pct: string;
  cost_split_pct: string;
  impl_fee_currency: string;
}

const LABELS = {
  agreement_id: "Agreement",
  effective_date: "Effective date",
  impl_split_pct: "Implementation split",
  recurring_split_pct: "Recurring split",
  cost_split_pct: "Cost split",
  impl_fee_currency: "Implementation fee currency",
} as const;

/**
 * Blank is "this term prices no implementation fee" — the ordinary state — and
 * it is a different statement from either currency. Modelled as an empty option
 * rather than a checkbox for the same reason the cost split is: the absence is a
 * legitimate value of the field, not a mode the field is switched out of.
 */
const FEE_CURRENCY_OPTIONS: readonly SelectOption<string>[] = [
  { value: "", label: "No implementation fee priced" },
  ...Currency.options.map((currency) => ({ value: currency, label: currency })),
];

function draftFromRecord(term: AgreementTerm): TermDraft {
  return {
    agreement_id: term.agreement_id,
    effective_date: term.effective_date,
    impl_split_pct: textOf(term.impl_split_pct),
    recurring_split_pct: textOf(term.recurring_split_pct),
    cost_split_pct: textOf(term.cost_split_pct),
    impl_fee_currency: term.impl_fee_currency ?? "",
  };
}

function toWire(draft: TermDraft): Wire {
  return {
    agreement_id: draft.agreement_id,
    effective_date: draft.effective_date,
    impl_split_pct: parseDecimal(draft.impl_split_pct),
    recurring_split_pct: parseDecimal(draft.recurring_split_pct),
    // Blank is null — "cost splitting is not part of this agreement" — which is
    // a different statement from 0%, "we bear none of it".
    cost_split_pct: parseOptionalDecimal(draft.cost_split_pct),
    // Same blank-is-null rule, and the server refuses the clear while bands
    // still exist rather than silently dropping a priced schedule.
    impl_fee_currency: draft.impl_fee_currency === "" ? null : draft.impl_fee_currency,
  };
}

function formatValue(field: string, value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "number" && field.endsWith("_pct")) return formatPercent(value);
  return String(value);
}

export interface AgreementTermFormDialogProps {
  agreementId: string;
  agreementName: string;
  /** Absent when adding a term. */
  term?: AgreementTerm;
  onClose: () => void;
}

export function AgreementTermFormDialog({
  agreementId,
  agreementName,
  term,
  onClose,
}: AgreementTermFormDialogProps): ReactNode {
  const mutations = useAgreementTermMutations();
  const isNew = term === undefined;
  const today = todayIsoDate();

  const editor = useEditor<TermDraft, AgreementTermCreate, AgreementTermUpdate, AgreementTerm>({
    mode: term === undefined ? { kind: "create" } : { kind: "update", record: term },
    blankDraft: {
      agreement_id: agreementId,
      effective_date: "",
      impl_split_pct: "",
      recurring_split_pct: "",
      cost_split_pct: "",
      impl_fee_currency: "",
    },
    draftFromRecord,
    toWire,
    createSchema: AgreementTermCreate,
    updateSchema: AgreementTermUpdate,
    create: (body, idempotencyKey) => mutations.create.mutateAsync({ body, idempotencyKey }),
    update: (id, body, version) => mutations.update.mutateAsync({ id, body, version }),
    fetchCurrent: mutations.fetchCurrent,
    onSaved: onClose,
  });

  const effective = editor.draft.effective_date;
  const isFuture = effective !== "" && effective > today;

  return (
    <FormDialog
      open
      onClose={onClose}
      title={isNew ? "Add a term" : "Edit term"}
      description={
        isNew
          ? "A term is a dated set of split percentages. Give it a future effective date to schedule a step-down or an escalator — the term in force stays the latest one dated on or before today."
          : "Correcting a term does not restate anything already invoiced — a bill freezes its split when it is marked sent or paid. It DOES re-price every bill that has not gone out yet, on the next read, which is why a step-up belongs in a new term rather than in an edit to this one."
      }
      submitLabel={isNew ? "Add term" : "Save changes"}
      editor={editor}
      labels={LABELS}
      formatValue={formatValue}
    >
      <ReadOnlyField
        label={LABELS.agreement_id}
        value={agreementName}
        reason="A term belongs to the agreement it was negotiated under."
      />
      <DateField
        label={LABELS.effective_date}
        required
        value={editor.draft.effective_date}
        error={editor.errors["effective_date"]}
        hint={
          isFuture
            ? "Future-dated: this term is scheduled and does not affect anything until that day."
            : "On or before today, so this term takes force as soon as it is saved — if it is the latest one."
        }
        onChange={(value) => editor.setField("effective_date", value)}
      />
      <NumberField
        label={LABELS.impl_split_pct}
        required
        suffix="%"
        hint="Our share of one-time implementation fees."
        value={editor.draft.impl_split_pct}
        error={editor.errors["impl_split_pct"]}
        onChange={(value) => editor.setField("impl_split_pct", value)}
      />
      <NumberField
        label={LABELS.recurring_split_pct}
        required
        suffix="%"
        hint="Our share of recurring revenue — SaaS, minutes, overage."
        value={editor.draft.recurring_split_pct}
        error={editor.errors["recurring_split_pct"]}
        onChange={(value) => editor.setField("recurring_split_pct", value)}
      />
      <NumberField
        label={LABELS.cost_split_pct}
        suffix="%"
        hint="Leave blank if cost splitting is not part of this agreement. Blank and 0% are different statements."
        value={editor.draft.cost_split_pct}
        error={editor.errors["cost_split_pct"]}
        onChange={(value) => editor.setField("cost_split_pct", value)}
      />
      <SelectField
        label={LABELS.impl_fee_currency}
        value={editor.draft.impl_fee_currency}
        options={FEE_CURRENCY_OPTIONS}
        error={editor.errors["impl_fee_currency"]}
        hint={
          isNew
            ? "Set this to price a banded implementation ('buy-in') fee under this term. It is the currency of the AGREEMENT's fee schedule, which need not be the currency its deals are booked in — the report converts."
            : "Changing this re-denominates every band under this term. It cannot be cleared while bands still exist."
        }
        onChange={(value) => editor.setField("impl_fee_currency", value)}
      />
    </FormDialog>
  );
}
